import { useEffect, useRef, useState } from "react";
import { CircleAlert, CircleCheck, FileText, X } from "lucide-react";
import {
  getResearchDocument,
  getResearchSet,
  listStaffReportDrafts,
  RESEARCH_MAX_FILES,
  RESEARCH_MAX_FILE_BYTES,
  type StaffReportDraftListItem,
  type StaffReportResearchDocument,
  type StaffReportSourcePreview,
} from "@/api/staff_reports";
import { SafeMarkdown } from "@/components/markdown/SafeMarkdown";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PaginatedTableControls } from "@/components/ui/paginated-table";
import { emptyPagination } from "@/lib/jsonPagination";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export type ReusedReportSource = { document: StaffReportResearchDocument; researchSetId: number };
type PreviewTarget = { file: File } | ReusedReportSource;

export function researchDocumentLabel(status: string) {
  switch (status) {
    case "done":
      return "Ready";
    case "failed":
      return "Couldn't read";
    case "skipped":
      return "Skipped";
    case "selected":
      return "Selected · not uploaded";
    case "uploading":
      return "Uploading";
    case "queued":
      return "Waiting to read";
    case "retrying":
      return "Retrying";
    default:
      return "Reading";
  }
}

export function StaffReportSources({
  municipalityId,
  files,
  onFilesChange,
  reused,
  onReusedChange,
  documents,
  researchSetId,
  locked,
  uploading = false,
  hideHeading = false,
}: {
  municipalityId: number;
  files: File[];
  onFilesChange: (files: File[]) => void;
  reused: ReusedReportSource[];
  onReusedChange: (sources: ReusedReportSource[]) => void;
  documents: StaffReportResearchDocument[];
  researchSetId?: number;
  locked: boolean;
  uploading?: boolean;
  hideHeading?: boolean;
}) {
  const [error, setError] = useState<string | null>(null);
  const [reuseOpen, setReuseOpen] = useState(false);
  const [reports, setReports] = useState<StaffReportDraftListItem[]>([]);
  const [reportsPage, setReportsPage] = useState(1);
  const [reportsPagination, setReportsPagination] = useState(() => emptyPagination(50));
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState<string | null>(null);
  const [reportsRetry, setReportsRetry] = useState(0);
  const [sourceSetId, setSourceSetId] = useState<number | null>(null);
  const [candidates, setCandidates] = useState<StaffReportResearchDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<PreviewTarget | null>(null);
  const previewOpener = useRef<HTMLButtonElement | null>(null);
  const reuseOpener = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!reuseOpen) return;
    let cancelled = false;
    setReportsLoading(true);
    setReportsError(null);
    setReports([]);
    listStaffReportDrafts({ municipalityId, page: reportsPage, withSources: true })
      .then((list) => {
        if (cancelled) return;
        setReports(list.drafts);
        setReportsPagination(list.pagination);
      })
      .catch((e) => {
        if (!cancelled) setReportsError(e instanceof Error ? e.message : "Could not load reports.");
      })
      .finally(() => { if (!cancelled) setReportsLoading(false); });
    return () => { cancelled = true; };
  }, [municipalityId, reuseOpen, reportsPage, reportsRetry]);

  useEffect(() => {
    if (!reuseOpen) return;
    if (!sourceSetId) {
      setCandidates([]);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setCandidates([]);
    getResearchSet(sourceSetId, municipalityId)
      .then((set) => {
        if (!cancelled) setCandidates(set.documents);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Could not load supporting files.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sourceSetId, municipalityId, reuseOpen]);

  const uploaded = researchSetId != null;
  const count = uploaded ? documents.length : files.length + reused.length;
  const cards = uploaded
    ? documents.map((document) => ({
        key: `doc-${document.id}`,
        name: document.original_filename,
        status: document.status,
        error: document.error_message,
        target: { document, researchSetId } as PreviewTarget,
      }))
    : [
        ...reused.map((source) => ({
          key: `reuse-${source.document.id}`,
          name: source.document.original_filename,
          status: "done",
          error: null,
          target: source as PreviewTarget,
        })),
        ...files.map((file, index) => ({
          key: `new-${index}`,
          name: file.name,
          status: uploading ? "uploading" : "selected",
          error: null,
          target: { file } as PreviewTarget,
        })),
      ];

  return (
    <section className="@container space-y-3" aria-label="Supporting files">
      {(!hideHeading || !locked) && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          {!hideHeading && (
            <h3 className="text-base font-semibold">
              Supporting files <span className="text-muted-foreground font-normal">({count})</span>
            </h3>
          )}
          {!locked && (
            <Button
              size="sm"
              variant="outline"
              ref={reuseOpener}
              onClick={() => {
                setError(null);
                setReuseOpen(true);
              }}
            >
              Reuse files from a report
            </Button>
          )}
        </div>
      )}
      {!locked && (
        <>
          <p className="text-sm text-muted-foreground">
            Up to {RESEARCH_MAX_FILES} PDF, Word (.docx), text, or Markdown files, 50 MB each. New and reused files
            count together.
          </p>
          <p className="text-xs text-muted-foreground">
            These files stay within reports. They are not automatically added to File Center or Research Assistant.
          </p>
          <div className="space-y-2">
            <label htmlFor="research-files" className="text-sm font-medium">
              Upload your research
            </label>
            <Input
              id="research-files"
              type="file"
              multiple
              accept=".pdf,.docx,.txt,.md"
              disabled={uploading}
              onChange={(event) => {
                const incoming = Array.from(event.currentTarget.files || []);
                event.currentTarget.value = "";
                if (files.length + reused.length + incoming.length > RESEARCH_MAX_FILES) {
                  setError(
                    `Choose at most ${RESEARCH_MAX_FILES} files, including reused files. Your current selection has been kept.`,
                  );
                  return;
                }
                const invalid = incoming.filter(
                  (file) => !/\.(pdf|docx|txt|md)$/i.test(file.name) || file.size > RESEARCH_MAX_FILE_BYTES,
                );
                setError(
                  invalid.length
                    ? `Not added: ${invalid.map((file) => file.name).join(", ")}. Use PDF, DOCX, TXT, or MD files up to 50 MB each.`
                    : null,
                );
                onFilesChange([...files, ...incoming.filter((file) => !invalid.includes(file))]);
              }}
            />
          </div>
        </>
      )}
      {error && !reuseOpen && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      {cards.length === 0 && (
        <p className="rounded-md border border-dashed p-5 text-sm text-muted-foreground">
          {locked
            ? "No supporting files are associated with this report."
            : "Add supporting files or reuse files from a saved report."}
        </p>
      )}
      <div
        className={`grid grid-cols-1 gap-2 @sm:grid-cols-2 @lg:grid-cols-3 ${locked ? "ordinal-scrollbar max-h-80 overflow-y-auto p-1" : ""}`}
        role={locked && cards.length > 0 ? "region" : undefined}
        aria-label={locked && cards.length > 0 ? "Supporting file list" : undefined}
        tabIndex={locked && cards.length > 0 ? 0 : undefined}
      >
        {cards.map((card) => (
          <div key={card.key} className="relative flex min-w-0 flex-col rounded-md border bg-background">
            <button
              type="button"
              className={`flex min-h-32 w-full min-w-0 flex-1 flex-col items-start gap-2 rounded-md p-3 text-left hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${!locked && !uploaded ? "pr-9" : ""}`}
              onClick={(event) => {
                previewOpener.current = event.currentTarget;
                setPreview(card.target);
              }}
              aria-label={`Preview ${card.name}`}
            >
              <span className="flex items-center gap-2 text-xs font-medium uppercase text-muted-foreground">
                <FileText aria-hidden className="h-4 w-4" />
                {card.name.split(".").pop()}
              </span>
              <span className="w-full text-sm font-medium [overflow-wrap:anywhere]">{card.name}</span>
              {card.error && (
                <span className="w-full text-xs text-destructive [overflow-wrap:anywhere]" role="alert">
                  {card.error}
                </span>
              )}
              <span className="mt-auto pt-1">
                <Badge
                  variant="outline"
                  className={`gap-1.5 border-transparent font-normal ${
                    card.status === "done"
                      ? "bg-[#E2F4D6] text-[#315923] dark:bg-[#283E20] dark:text-[#CBEBB5]"
                      : card.status === "failed"
                        ? "bg-[#F9E2E2] text-[#8F3030] dark:bg-[#4A2929] dark:text-[#EFADAD]"
                        : card.status === "selected" || card.status === "skipped"
                          ? "bg-muted text-muted-foreground"
                          : "bg-[#E6EEFB] text-[#254B83] dark:bg-[#20344F] dark:text-[#B8D1F5]"
                  }`}
                >
                  {card.status === "done" && <CircleCheck aria-hidden className="h-3 w-3 shrink-0" />}
                  {card.status === "failed" && <CircleAlert aria-hidden className="h-3 w-3 shrink-0" />}
                  {researchDocumentLabel(card.status)}
                </Badge>
              </span>
            </button>
            {!locked && !uploaded && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1 h-7 w-7"
                aria-label={`Remove ${card.name}`}
                onClick={() => {
                  const target = card.target;
                  if ("file" in target) onFilesChange(files.filter((file) => file !== target.file));
                  else onReusedChange(reused.filter((source) => source.document.id !== target.document.id));
                }}
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
        ))}
      </div>
      <Dialog open={reuseOpen} onOpenChange={setReuseOpen}>
        <DialogContent
          className="max-h-[85vh] overflow-y-auto"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            reuseOpener.current?.focus();
          }}
        >
          <DialogHeader>
            <DialogTitle>Reuse supporting files</DialogTitle>
            <DialogDescription>
              Select files from a saved report in this municipality. The original report stays unchanged.
            </DialogDescription>
          </DialogHeader>
          <label className="text-sm font-medium" htmlFor="reuse-report">
            Report
          </label>
          <select
            id="reuse-report"
            className="w-full rounded-md border bg-background p-2 text-sm"
            value={sourceSetId ?? ""}
            disabled={reportsLoading}
            onChange={(event) => setSourceSetId(Number(event.target.value) || null)}
          >
            <option value="">Choose a report</option>
            {reports.map((draft) => (
              <option key={draft.id} value={draft.staff_report_research_set_id!}>
                {draft.name}
              </option>
            ))}
          </select>
          {reportsLoading && <p role="status" className="text-sm">Loading reports…</p>}
          {reportsError && <div role="alert" className="text-sm text-destructive">{reportsError} <Button size="sm" variant="outline" onClick={() => setReportsRetry((value) => value + 1)}>Retry loading reports</Button></div>}
          {!reportsLoading && !reportsError && reports.length === 0 && (
            <p className="text-sm text-muted-foreground">No saved reports with supporting files yet.</p>
          )}
          {reportsPagination.total_pages > 1 && <PaginatedTableControls
            variant="compact"
            currentPage={reportsPagination.current_page}
            totalPages={reportsPagination.total_pages}
            perPage={reportsPagination.per_page}
            totalCount={reportsPagination.total_count}
            onPageChange={(page) => {
              setSourceSetId(null);
              setCandidates([]);
              setReportsPage(page);
            }}
          />}
          {loading && (
            <p role="status" className="text-sm">
              Loading files…
            </p>
          )}
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          {!loading &&
            candidates.map((document) => (
              <label key={document.id} className="flex items-start gap-3 rounded-md border p-3 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={reused.some((source) => source.document.id === document.id)}
                  disabled={document.status !== "done"}
                  onChange={(event) => {
                    if (!event.target.checked) {
                      onReusedChange(reused.filter((source) => source.document.id !== document.id));
                      return;
                    }
                    if (files.length + reused.length >= RESEARCH_MAX_FILES) {
                      setError(`Choose at most ${RESEARCH_MAX_FILES} files, including new uploads.`);
                      return;
                    }
                    if (sourceSetId) onReusedChange([...reused, { document, researchSetId: sourceSetId }]);
                    setError(null);
                  }}
                />
                <span className="min-w-0 break-words">
                  {document.original_filename}
                  <span className="block text-xs text-muted-foreground">
                    {document.extension?.toUpperCase()} · {researchDocumentLabel(document.status)}
                  </span>
                </span>
              </label>
            ))}
          <Button onClick={() => setReuseOpen(false)}>Done selecting files</Button>
        </DialogContent>
      </Dialog>
      <Dialog
        open={preview != null}
        onOpenChange={(open) => {
          if (!open) setPreview(null);
        }}
      >
        <DialogContent
          className="max-w-3xl max-h-[90vh] flex flex-col"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            previewOpener.current?.focus();
          }}
        >
          {preview && (
            <SourcePreview
              key={"file" in preview ? preview.file.name : preview.document.id}
              target={preview}
              municipalityId={municipalityId}
            />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function SourcePreview({ target, municipalityId }: { target: PreviewTarget; municipalityId: number }) {
  const [detail, setDetail] = useState<StaffReportSourcePreview | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const file = "file" in target ? target.file : null;
  const name = file ? file.name : (target as ReusedReportSource).document.original_filename;
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    const load = async () => {
      try {
        if (file) {
          objectUrl = URL.createObjectURL(file);
          setUrl(objectUrl);
          if (/\.(txt|md)$/i.test(file.name)) {
            const value = await file.text();
            if (!cancelled) setText(value);
          }
        } else {
          const source = target as ReusedReportSource;
          const value = await getResearchDocument(source.researchSetId, source.document.id, municipalityId);
          if (!cancelled) setDetail(value);
        }
      } catch (e) {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "The preview is unavailable. Try opening the file again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [target, municipalityId, file]);
  const content = detail?.status === "done" ? detail.parsed_markdown : text;
  return (
    <>
      <DialogHeader>
        <DialogTitle className="break-words pr-5">{name}</DialogTitle>
        <DialogDescription>
          {file
            ? "Selected file · not uploaded yet"
            : error
              ? "Preview unavailable"
              : detail
                ? researchDocumentLabel(detail.status)
                : "Loading file"}
        </DialogDescription>
      </DialogHeader>
      {(detail?.download_url || url) && (
        <a href={detail?.download_url || url!} download={name} className="text-sm underline underline-offset-4">
          Download original
        </a>
      )}
      <div className="min-h-0 overflow-y-auto space-y-3">
        {loading && <p role="status">Loading preview…</p>}
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        {content && (
          <>
            <p className="text-xs text-muted-foreground">
              {detail ? "Extracted text · original formatting may differ" : "Text preview"}
            </p>
            <div className="prose prose-sm dark:prose-invert max-w-none break-words">
              <SafeMarkdown>{content}</SafeMarkdown>
            </div>
          </>
        )}
        {file && /\.pdf$/i.test(name) && url && (
          <object data={url} type="application/pdf" aria-label={`PDF preview of ${name}`} className="w-full h-[55vh]">
            <p>PDF preview is unavailable in this browser. Download the original to inspect it.</p>
          </object>
        )}
        {!loading && !error && !content && !(file && /\.pdf$/i.test(name)) && (
          <p className="text-sm text-muted-foreground">
            {file
              ? /\.docx$/i.test(name)
                ? "A text preview will be available after this Word file is uploaded and read. You can download the original now."
                : "This file has no readable text. Download the original to inspect it."
              : detail?.status === "done"
                ? "No extracted text is available. Download the original to inspect it."
                : detail?.error_message || "This file has not been read yet. A preview is not available."}
          </p>
        )}
      </div>
    </>
  );
}
